import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { sessionStore } from '../session/session-store';
import { SessionManager } from '../session/sessions';
import { scrollToMessage } from './conversation';
import { clearConversationSearch, setConversationSearchHighlightedSessionId } from './conversation-search-ui';

const DEBOUNCE_MS = 300;

type SearchHit = {
  sessionId: string;
  sessionTitle: string;
  messageId: string;
  role: string;
  snippet: string;
  updatedAt: number;
};

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function authHeaders(): Record<string, string> {
  return { Authorization: 'Bearer ' + state.authToken };
}

function getResultsEl(): HTMLElement | null {
  return $('conversation-search-results');
}

function hideResults() {
  const el = getResultsEl();
  if (el) {
    el.classList.add('hidden');
  }
}

function renderResults(hits: SearchHit[]) {
  const container = getResultsEl();
  if (!container) return;

  container.textContent = '';
  if (hits.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'conversation-search-empty';
    empty.textContent = 'Ничего не найдено';
    container.appendChild(empty);
    container.classList.remove('hidden');
    return;
  }

  hits.forEach((hit) => {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'conversation-search-result';
    row.innerHTML = `<span class="conversation-search-result-title">${escapeHtml(hit.sessionTitle)}</span>
      <span class="conversation-search-result-snippet">${escapeHtml(hit.snippet)}</span>`;
    row.addEventListener('click', () => navigateToHit(hit));
    container.appendChild(row);
  });
  container.classList.remove('hidden');
}

function escapeHtml(value: string) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

async function navigateToHit(hit: SearchHit) {
  setConversationSearchHighlightedSessionId(hit.sessionId);
  hideResults();

  if (sessionStore.activeSessionId !== hit.sessionId) {
    await SessionManager.loadSession(hit.sessionId);
  }

  const input = $<HTMLInputElement>('conversation-search-input');
  const query = input ? input.value.trim() : '';

  requestAnimationFrame(() => {
    if (hit.messageId && scrollToMessage(hit.messageId, query)) return;
    const session = sessionStore.getActiveSession();
    const needle = hit.snippet.trim().slice(0, 80);
    const msg = session?.history.find((m) => needle && m.content.includes(needle));
    if (msg?.id && scrollToMessage(msg.id, query)) return;
    showToast('Сообщение не найдено в текущем виде разговора', 'info');
  });
}

async function runSearch(query: string) {
  if (!state.authToken || !query.trim()) {
    hideResults();
    return;
  }
  try {
    const r = await fetch(`/api/sessions/search?q=${encodeURIComponent(query)}&limit=20`, {
      headers: authHeaders(),
    });
    if (!r.ok) {
      hideResults();
      return;
    }
    const data = await r.json() as { results?: SearchHit[] };
    renderResults(data.results || []);
  } catch (err) {
    console.error('Search failed', err);
    hideResults();
  }
}

export function initConversationSearch() {
  const input = $<HTMLInputElement>('conversation-search-input');
  if (!input || input.dataset.bound) return;
  input.dataset.bound = '1';

  const searchField = document.querySelector('.conversation-search-field');
  searchField?.addEventListener('click', (ev) => {
    const sidebar = $('sidebar');
    if (sidebar && sidebar.classList.contains('collapsed')) {
      ev.preventDefault();
      ev.stopPropagation();
      sidebar.classList.remove('collapsed');
      input.focus();
    }
  });

  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!q) {
      clearConversationSearch();
      return;
    }
    debounceTimer = setTimeout(() => runSearch(q), DEBOUNCE_MS);
  });

  input.addEventListener('focus', () => {
    const q = input.value.trim();
    if (q) {
      const resultsEl = getResultsEl();
      if (resultsEl) {
        if (resultsEl.textContent) {
          resultsEl.classList.remove('hidden');
        } else {
          runSearch(q);
        }
      }
    }
  });

  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      clearConversationSearch();
    }
  });

  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const wrap = $('conversation-search-wrap');
    if (wrap && !wrap.contains(target)) {
      hideResults();
    }
  });
}

